import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useMyContext } from '../../../../context/MyContext';
import Sidebar from '../../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../../component/b2b/navBar/b2bNavBar';
import '../../../../css/custom-scrollbar.css';
import { Base_Url, b2bRateCard } from '../../../../config/config';
import { FcMoneyTransfer } from "react-icons/fc";
import { toast } from 'react-toastify';
import { Truck, Package, MapPin, ChevronDown, Layers, Receipt, IndianRupee, Sparkles } from 'lucide-react';



const B2BRateCard = () => {
  const [zoneMetrics, setZoneMetrics] = useState({});
  const [showZoneInfo, setShowZoneInfo] = useState({});
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [data, setData] = useState([]);
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [showVasCharges, setShowVasCharges] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cus_id = localStorage.getItem('cus_id');

  const zoneMapping = {
    'n1': 'N1', 'n2': 'N2', 'n3': 'N3', 'n4': 'N4',
    'c1': 'C1', 'c2': 'C2',
    'w1': 'W1', 'w2': 'W2',
    's1': 'S1', 's2': 'S2', 's3': 'S3', 's4': 'S4',
    'e1': 'E1', 'e2': 'E2',
    'ne1': 'NE1', 'ne2': 'NE2'
  };

  useEffect(() => {
    fetchRateCardList();
  }, []);

  const handleNavigation = () => {
    navigate('/dashboard');
  };

  const fetchRateCardList = async () => {
    try {
      setLoading(true);
      const url = Base_Url + b2bRateCard;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token || '',
          cus_id: cus_id || '',
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();

      if (result.status === "SUCCESS") {
        setData(result.data);
        setZoneMetrics(result.zonemetrics || {});
        if (result.data.length > 0) {
          setActivePartner(result.data[0].partner);
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Error fetching rate card data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const [activePartner, setActivePartner] = useState(data.length > 0 ? data[0].partner : null);

  const parsePricingData = (pricingString) => {
    try {
      return JSON.parse(pricingString);
    } catch (error) {
      console.error('Error parsing pricing data:', error);
      return {};
    }
  };

  const parseVasData = (vasString) => {
    try {
      return JSON.parse(vasString.trim());
    } catch (error) {
      console.error('Error parsing VAS data:', error);
      return [];
    }
  };

  const getZonePricing = (pricingData, fromZone, toZone) => {
    if (!pricingData[fromZone] || !pricingData[fromZone][toZone]) {
      return '-';
    }
    return pricingData[fromZone][toZone];
  };

  const formatChargeValue = (charge) => {
    if (charge.charge_type === 'percent') {
      return `${charge.value}%`;
    } else if (charge.charge_type === 'per_kg') {
      return `₹${charge.value}/kg`;
    } else {
      return `₹${charge.value}`;
    }
  };

  const getPartnerLogo = (partner) => {
    const logos = {
      'delhivery': '/assets/images/Delhivery.png',
      'gati': '/assets/images/Gati.jpg',
      'movin': '/assets/images/movin.svg'
    };
    return logos[partner.toLowerCase()] || '/assets/images/delivary.png';
  };

  const handlePartnerClick = (partner) => {
    setExpandedPartner(expandedPartner === partner ? null : partner);
  };

  const toggleVasCharges = (partner) => {
    setShowVasCharges(prev => ({
      ...prev,
      [partner]: !prev[partner]
    }));
  };

  const toggleZoneInfo = () => {
    setShowZoneInfo(prev => ({
      ...prev,
      [activePartner]: !prev[activePartner]
    }));
  };

  const getZoneColor = (zone) => {
    const colors = {
      'N1': 'bg-blue-50 text-blue-700 border-blue-200',
      'N2': 'bg-blue-50 text-blue-700 border-blue-200',
      'N3': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'N4': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'C1': 'bg-violet-50 text-violet-700 border-violet-200',
      'C2': 'bg-violet-50 text-violet-700 border-violet-200',
      'W1': 'bg-pink-50 text-pink-700 border-pink-200',
      'W2': 'bg-pink-50 text-pink-700 border-pink-200',
      'S1': 'bg-red-50 text-red-700 border-red-200',
      'S2': 'bg-orange-50 text-orange-700 border-orange-200',
      'S3': 'bg-amber-50 text-amber-700 border-amber-200',
      'S4': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'E1': 'bg-green-50 text-green-700 border-green-200',
      'E2': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'NE1': 'bg-teal-50 text-teal-700 border-teal-200',
      'NE2': 'bg-cyan-50 text-cyan-700 border-cyan-200'
    };
    return colors[zone] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Group zones by region for mobile card view
  const regionGroups = [
    { title: 'North', zones: ['n1', 'n2', 'n3', 'n4'], color: 'blue' },
    { title: 'Central', zones: ['c1', 'c2'], color: 'violet' },
    { title: 'West', zones: ['w1', 'w2'], color: 'pink' },
    { title: 'South', zones: ['s1', 's2', 's3', 's4'], color: 'orange' },
    { title: 'East', zones: ['e1', 'e2'], color: 'green' },
    { title: 'Northeast', zones: ['ne1', 'ne2'], color: 'teal' },
  ];

  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

      <NavBar />

      <div className="flex flex-1 overflow-hidden">
        <div
          className={`${isToggleSidebar ? "md:w-64" : "md:w-20"} w-0 m-0 md:m-3 md:rounded-3xl ${glass} overflow-visible md:overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0`}
        >
          <Sidebar />
        </div>

        <div className={`w-full flex flex-1 flex-col custom-scrollbar overflow-y-auto ${isDarkMode ? 'dark:bg-darkmodeColor text-white' : 'bg-gray-100'}`}>
          <div className="p-3 sm:p-4 max-w-[1500px] mx-auto w-full">

            {/* ===== HEADER ===== */}
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg shadow-indigo-900/50' : 'bg-gradient-to-br from-red-500 to-red-600 shadow-md shadow-red-200'}`}>
                  <FcMoneyTransfer size={20} className="text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className={`text-lg sm:text-2xl font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Rate Card Dashboard
                  </h1>
                  <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                    Zone pricing, VAS charges and courier partner rates
                  </p>
                </div>
              </div>
            </motion.div>

            {loading ? (
              <div className={`flex flex-col items-center justify-center h-64 rounded-2xl border ${isDarkMode ? 'border-indigo-500/25 dark:bg-darkmodeColor' : 'border-gray-200 bg-white'}`}>
                <div className="relative h-12 w-12">
                  <div className={`absolute inset-0 rounded-full border-4 ${isDarkMode ? 'border-indigo-500/20' : 'border-gray-100'}`}></div>
                  <div className={`absolute inset-0 rounded-full border-4 border-transparent animate-spin ${isDarkMode ? 'border-t-indigo-400' : 'border-t-red-500'}`}></div>
                </div>
                <p className={`mt-4 text-sm font-medium ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>Fetching rate cards...</p>
              </div>
            ) : data.length === 0 ? (
              <div className={`rounded-2xl p-8 sm:p-12 text-center border ${isDarkMode ? 'border-indigo-500/25 dark:bg-darkmodeColor' : 'border-gray-200 bg-white'}`}>
                <div className={`mx-auto mb-4 w-16 h-16 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-indigo-500/10' : 'bg-gray-100'}`}>
                  <Package className={isDarkMode ? 'text-indigo-400/50' : 'text-gray-400'} size={28} />
                </div>
                <h3 className={`text-base font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No rate cards available</h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  There are no courier partner rate cards to display at this time.
                </p>
              </div>
            ) : (
              <>
                {/* ===== PARTNER TABS ===== */}
                <div className="mb-4">
                  <h3 className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                    Select Courier Partner
                  </h3>
                  <div className="flex gap-2.5 overflow-x-auto pb-1 custom-scrollbar sm:grid sm:grid-cols-3">
                    {data.slice(0, 3).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setActivePartner(item.partner)}
                        className={`relative flex-shrink-0 min-w-[130px] sm:min-w-0 rounded-xl px-4 py-3 text-left transition-all border-2
                          ${activePartner === item.partner
                            ? isDarkMode
                              ? 'bg-gradient-to-br from-indigo-600 to-purple-600 border-transparent shadow-lg shadow-indigo-900/40 text-white'
                              : 'bg-gradient-to-br from-gray-900 to-gray-800 border-transparent shadow-lg shadow-gray-300 text-white'
                            : isDarkMode
                              ? 'bg-indigo-500/5 border-indigo-500/25 text-gray-300 hover:border-indigo-400/50'
                              : 'bg-white border-gray-200 hover:border-gray-400 hover:shadow-sm'
                          }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className={`text-[10px] uppercase tracking-wider font-bold ${activePartner === item.partner ? 'text-white/70' : isDarkMode ? 'text-indigo-300/50' : 'text-gray-400'}`}>
                            Partner
                          </p>
                          {activePartner === item.partner && (
                            <Sparkles size={12} className="text-yellow-300 flex-shrink-0" />
                          )}
                        </div>
                        <span className={`flex items-center gap-2 text-sm sm:text-base font-bold capitalize ${activePartner === item.partner ? 'text-white' : isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${activePartner === item.partner ? 'bg-emerald-400' : isDarkMode ? 'bg-indigo-400/50' : 'bg-gray-300'}`} />
                          {item.partner}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ===== ZONE INFO SECTION ===== */}
                {Object.keys(zoneMetrics).length > 0 && (
                  <div className={`rounded-2xl overflow-hidden mb-4 border ${isDarkMode ? 'border-indigo-500/25' : 'border-gray-200'}`}>
                    <button
                      onClick={toggleZoneInfo}
                      className={`w-full px-4 py-3 flex items-center justify-between transition-all ${isDarkMode ? 'bg-indigo-500/10 text-white hover:bg-indigo-500/15' : 'bg-white text-gray-900 hover:bg-gray-50'}`}
                    >
                      <span className="font-bold flex items-center gap-2 text-sm sm:text-base">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-50'}`}>
                          <MapPin className={isDarkMode ? 'text-indigo-300' : 'text-indigo-600'} size={14} />
                        </div>
                        Zone Information & Coverage
                        <span className={`ml-1 text-[10px] px-2 py-0.5 rounded-full font-bold ${isDarkMode ? 'bg-indigo-500/20 text-indigo-200' : 'bg-indigo-100 text-indigo-700'}`}>
                          {Object.keys(zoneMetrics).length}
                        </span>
                      </span>
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-300 ${isDarkMode ? 'text-indigo-300' : 'text-gray-400'} ${showZoneInfo[activePartner] ? 'rotate-180' : ''}`}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {showZoneInfo[activePartner] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className={`${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'} overflow-hidden`}
                        >
                          <div className="p-3 space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
                            {Object.entries(zoneMetrics).map(([zone, locations], idx) => (
                              <div
                                key={zone}
                                className={`rounded-xl border p-3 ${isDarkMode ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-gray-50 border-gray-200'}`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`px-2.5 py-1 rounded-md font-bold text-xs border ${getZoneColor(zone)}`}>
                                    {zone}
                                  </span>
                                  <span className={`text-[11px] font-semibold ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                                    {locations.length} areas
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {locations.map((location, locIdx) => (
                                    <span
                                      key={locIdx}
                                      className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${isDarkMode ? 'bg-indigo-500/10 text-indigo-200 border border-indigo-500/20' : 'bg-white text-gray-600 border border-gray-200'}`}
                                    >
                                      {location}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {/* ===== CONTENT FOR ACTIVE PARTNER ===== */}
                {(() => {
                  const activeItem = data.find(item => item.partner === activePartner);
                  if (!activeItem) return null;
                  const pricingData = parsePricingData(activeItem.zone_pricing);
                  const vasCharges = parseVasData(activeItem.vas_pricing);
                  const activeCharges = vasCharges.filter(charge => charge.is_active);
                  const zones = Object.keys(pricingData);

                  return (
                    <motion.div
                      key={activePartner}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className={`${isDarkMode ? 'dark:bg-darkmodeColor border-indigo-500/25' : 'bg-white border-gray-200'} rounded-2xl overflow-hidden border`}
                    >
                      {/* Partner Header */}
                      <div className={`px-4 py-4 flex items-center justify-between gap-3 border-b ${isDarkMode ? 'bg-indigo-500/5 border-indigo-500/25' : 'bg-gradient-to-r from-gray-50 to-white border-gray-100'}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2 rounded-xl flex-shrink-0 ${isDarkMode ? 'bg-white/5 border border-indigo-500/30' : 'bg-white border border-gray-200 shadow-sm'}`}>
                            <img
                              src={getPartnerLogo(activeItem.partner)}
                              alt={`${activeItem.partner} logo`}
                              className="w-10 h-10 object-contain"
                            />
                          </div>
                          <div className="min-w-0">
                            <h2 className={`text-lg sm:text-xl font-bold capitalize truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {activeItem.partner}
                            </h2>
                            <div className={`inline-flex items-center gap-1 text-xs mt-0.5 font-medium ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                              <Layers size={11} />
                              {zones.length} zones available
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-3 sm:p-4 space-y-4">

                        {/* ===== VAS CHARGES SECTION ===== */}
                        {activeCharges.length > 0 && (
                          <div className={`rounded-xl overflow-hidden border ${isDarkMode ? 'border-indigo-500/25' : 'border-gray-200'}`}>
                            <button
                              onClick={() => toggleVasCharges(activeItem.partner)}
                              className={`w-full px-4 py-3 flex items-center justify-between transition-all ${isDarkMode ? 'bg-indigo-500/10 text-white hover:bg-indigo-500/15' : 'bg-gray-50 text-gray-900 hover:bg-gray-100'}`}
                            >
                              <span className="font-bold flex items-center gap-2 text-sm">
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-indigo-500/20' : 'bg-white border border-gray-200'}`}>
                                  <Receipt className={isDarkMode ? 'text-indigo-300' : 'text-indigo-600'} size={14} />
                                </div>
                                VAS Charges
                                <span className={`ml-1 text-[10px] px-2 py-0.5 rounded-full font-bold ${isDarkMode ? 'bg-indigo-500/20 text-indigo-200' : 'bg-indigo-100 text-indigo-700'}`}>
                                  {activeCharges.length}
                                </span>
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 transition-transform duration-300 ${isDarkMode ? 'text-indigo-300' : 'text-gray-400'} ${showVasCharges[activeItem.partner] ? 'rotate-180' : ''}`}
                              />
                            </button>

                            <AnimatePresence initial={false}>
                              {showVasCharges[activeItem.partner] && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.25 }}
                                  className={`overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}
                                >
                                  <div className="p-3 space-y-2">
                                    {activeCharges.map((charge, idx) => (
                                      <div
                                        key={idx}
                                        className={`rounded-xl border p-3 ${isDarkMode ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-gray-50 border-gray-100'}`}
                                      >
                                        {/* Top row: label + value */}
                                        <div className="flex items-start justify-between gap-3 mb-2">
                                          <span className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {charge.label}
                                          </span>
                                          <span className={`font-bold text-base flex-shrink-0 bg-gradient-to-r bg-clip-text text-transparent ${isDarkMode ? 'from-indigo-300 to-purple-300' : 'from-red-500 to-red-600'}`}>
                                            {formatChargeValue(charge)}
                                          </span>
                                        </div>
                                        {/* Tags row */}
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? 'bg-indigo-500/15 text-indigo-200 border border-indigo-500/20' : 'bg-white text-gray-600 border border-gray-200'}`}>
                                            {charge.charge_key}
                                          </span>
                                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold ${isDarkMode ? 'bg-purple-500/15 text-purple-200 border border-purple-500/20' : 'bg-purple-50 text-purple-700 border border-purple-200'}`}>
                                            {charge.charge_type}
                                          </span>
                                          {charge.condition_value && (
                                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                                              {charge.condition_value}
                                            </span>
                                          )}
                                          {charge.min_value && (
                                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                                              min ₹{charge.min_value}
                                            </span>
                                          )}
                                          {charge.max_value && (
                                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${isDarkMode ? 'text-amber-300/70' : 'text-amber-600'}`}>
                                              max ₹{charge.max_value}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )}

                        {/* ===== ZONE PRICING SECTION ===== */}
                        <div className={`rounded-xl overflow-hidden border ${isDarkMode ? 'border-indigo-500/25' : 'border-gray-200'}`}>
                          <div className={`px-4 py-3 flex items-center gap-2 border-b ${isDarkMode ? 'bg-indigo-500/10 border-indigo-500/25' : 'bg-gray-50 border-gray-100'}`}>
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-indigo-500/20' : 'bg-white border border-gray-200'}`}>
                              <IndianRupee className={isDarkMode ? 'text-indigo-300' : 'text-indigo-600'} size={14} />
                            </div>
                            <h3 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Zone Pricing Matrix
                            </h3>
                          </div>

                          {/* ===== DESKTOP TABLE (hidden below md) ===== */}
                          <div className="hidden md:block overflow-x-auto custom-scrollbar">
                            <table className="min-w-full">
                              <thead className={`${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
                                <tr>
                                  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold border-b ${isDarkMode ? 'border-indigo-500/25 text-indigo-300/70' : 'border-gray-200 text-gray-400'}`}>Zone</th>
                                  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold border-b ${isDarkMode ? 'border-indigo-500/25 text-indigo-300/70' : 'border-gray-200 text-gray-400'}`}>North (N1-N4)</th>
                                  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold border-b ${isDarkMode ? 'border-indigo-500/25 text-indigo-300/70' : 'border-gray-200 text-gray-400'}`}>Central (C1-C2)</th>
                                  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold border-b ${isDarkMode ? 'border-indigo-500/25 text-indigo-300/70' : 'border-gray-200 text-gray-400'}`}>West (W1-W2)</th>
                                  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold border-b ${isDarkMode ? 'border-indigo-500/25 text-indigo-300/70' : 'border-gray-200 text-gray-400'}`}>South (S1-S4)</th>
                                  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold border-b ${isDarkMode ? 'border-indigo-500/25 text-indigo-300/70' : 'border-gray-200 text-gray-400'}`}>East (E1-E2)</th>
                                  <th className={`px-4 py-2 text-left text-[11px] uppercase tracking-wider font-bold border-b ${isDarkMode ? 'border-indigo-500/25 text-indigo-300/70' : 'border-gray-200 text-gray-400'}`}>Northeast (NE1-NE2)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {zones.map((zone, zoneIndex) => (
                                  <tr
                                    key={zone}
                                    className={`${zoneIndex % 2 === 0
                                      ? isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'
                                      : isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-gray-50/60'
                                      } hover:bg-gray-50 dark:hover:bg-indigo-500/5 transition-colors`}
                                  >
                                    <td className={`px-4 py-2 border-b ${isDarkMode ? 'border-indigo-500/15' : 'border-gray-100'}`}>
                                      <span className={`font-bold text-sm px-2.5 py-0.5 rounded-md ${isDarkMode ? 'bg-indigo-500/20 text-indigo-100' : 'bg-gray-900 text-white'}`}>
                                        {zoneMapping[zone] || zone.toUpperCase()}
                                      </span>
                                    </td>
                                    <td className={`px-4 py-2 border-b ${isDarkMode ? 'border-indigo-500/15' : 'border-gray-100'}`}>
                                      <div className="grid grid-cols-2 gap-2 text-sm">
                                        {['n1', 'n2', 'n3', 'n4'].map(z => (
                                          <div key={z} className="flex items-center gap-1">
                                            <span className={`font-medium text-[11px] ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>{z.toUpperCase()}:</span>
                                            <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>₹{getZonePricing(pricingData, zone, z)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                    <td className={`px-4 py-2 border-b ${isDarkMode ? 'border-indigo-500/15' : 'border-gray-100'}`}>
                                      <div className="grid grid-cols-1 gap-2 text-sm">
                                        {['c1', 'c2'].map(z => (
                                          <div key={z} className="flex items-center gap-1">
                                            <span className={`font-medium text-[11px] ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>{z.toUpperCase()}:</span>
                                            <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>₹{getZonePricing(pricingData, zone, z)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                    <td className={`px-4 py-2 border-b ${isDarkMode ? 'border-indigo-500/15' : 'border-gray-100'}`}>
                                      <div className="grid grid-cols-1 gap-2 text-sm">
                                        {['w1', 'w2'].map(z => (
                                          <div key={z} className="flex items-center gap-1">
                                            <span className={`font-medium text-[11px] ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>{z.toUpperCase()}:</span>
                                            <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>₹{getZonePricing(pricingData, zone, z)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                    <td className={`px-4 py-2 border-b ${isDarkMode ? 'border-indigo-500/15' : 'border-gray-100'}`}>
                                      <div className="grid grid-cols-2 gap-2 text-sm">
                                        {['s1', 's2', 's3', 's4'].map(z => (
                                          <div key={z} className="flex items-center gap-1">
                                            <span className={`font-medium text-[11px] ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>{z.toUpperCase()}:</span>
                                            <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>₹{getZonePricing(pricingData, zone, z)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                    <td className={`px-4 py-2 border-b ${isDarkMode ? 'border-indigo-500/15' : 'border-gray-100'}`}>
                                      <div className="grid grid-cols-1 gap-2 text-sm">
                                        {['e1', 'e2'].map(z => (
                                          <div key={z} className="flex items-center gap-1">
                                            <span className={`font-medium text-[11px] ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>{z.toUpperCase()}:</span>
                                            <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>₹{getZonePricing(pricingData, zone, z)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                    <td className={`px-4 py-2 border-b ${isDarkMode ? 'border-indigo-500/15' : 'border-gray-100'}`}>
                                      <div className="grid grid-cols-1 gap-2 text-sm">
                                        {['ne1', 'ne2'].map(z => (
                                          <div key={z} className="flex items-center gap-1">
                                            <span className={`font-medium text-[11px] ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>{z.toUpperCase()}:</span>
                                            <span className={`font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>₹{getZonePricing(pricingData, zone, z)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* ===== MOBILE CARD VIEW (shown below md) ===== */}
                          <div className="md:hidden p-3 space-y-3">
                            {zones.map((zone) => (
                              <div
                                key={zone}
                                className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-indigo-500/25 bg-indigo-500/5' : 'border-gray-200 bg-gray-50'}`}
                              >
                                {/* Zone header */}
                                <div className={`px-3 py-2 flex items-center justify-between border-b ${isDarkMode ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-white border-gray-100'}`}>
                                  <span className={`font-bold text-sm px-2.5 py-1 rounded-md ${isDarkMode ? 'bg-indigo-500/30 text-indigo-100' : 'bg-gray-900 text-white'}`}>
                                    {zoneMapping[zone] || zone.toUpperCase()}
                                  </span>
                                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>
                                    From this zone
                                  </span>
                                </div>
                                {/* Region rows */}
                                <div className="p-2 space-y-1.5">
                                  {regionGroups.map((region) => (
                                    <div key={region.title} className={`rounded-lg p-2 ${isDarkMode ? 'bg-white/[0.03]' : 'bg-white'}`}>
                                      <p className={`text-[10px] font-bold uppercase tracking-wider mb-1.5 ${isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'}`}>
                                        To {region.title}
                                      </p>
                                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                        {region.zones.map(z => (
                                          <div key={z} className="flex items-center justify-between">
                                            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-indigo-300/60' : 'text-gray-400'}`}>
                                              {z.toUpperCase()}
                                            </span>
                                            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                                              ₹{getZonePricing(pricingData, zone, z)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

};

export default B2BRateCard;